import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ApiService } from '@api/api.service';
import { User, UserUpdate } from '@api/types';
import { CountryCode } from '@api/types/country_code';
import { TwoFactorMethod } from '@api/users/me/twoFactor/two-factor.service';
import { SMS_SUPPORTED_COUNTRY_CODES } from '@forms/phone-number/sms-suported-countries';
import { ModalService } from '@shared/modal/modal.service';
import { FormatService } from '@shared/services/format.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { StatusCodes } from '@shared/types/status-codes';
import { Subscription } from 'rxjs';
import { PhoneNumberValidator } from '../../../validator/phone-number.validator';

class UserDetailsForm {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;

  constructor(user: User, format: FormatService) {
    this.first_name = user.first_name;
    this.last_name = user.last_name;
    this.email = user.email;
    this.phone_number = user.phone_number;
  }
}

class UserPatch implements UserUpdate {
  first_name: string;
  last_name: string;
  email: string;
  enable_2fa: boolean;
  phone_number: string;

  constructor(userForm: UserDetailsForm, format: FormatService) {
    this.first_name = userForm.first_name;
    this.last_name = userForm.last_name;
    this.email = userForm.email;
    this.phone_number = userForm.phone_number;
  }
}

@Component({
  selector: 'rav-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit, OnDestroy {
  user: User;
  formGroup: FormGroup;
  updateProfileSuccessMessage: string;
  changePasswordSuccessMessage: string;
  twoFactorFormGroup: FormGroup;
  display2FactorMethod: boolean;
  subscription: Subscription = new Subscription();

  smsPhoneNumberForm = this.formBuilder.group({
    phoneNumber: ['', [Validators.required, PhoneNumberValidator.Validate]]
  });
  voicePhoneNumber: string;
  current2FAEnabled = false;
  supportedCountryCodes: CountryCode[] = SMS_SUPPORTED_COUNTRY_CODES;

  constructor(
    private api: ApiService,
    private localStorage: LocalStorageService,
    private formBuilder: FormBuilder,
    public modal: ModalService,
    private format: FormatService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  async ngOnInit() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;

    this.formGroup = this.formBuilder.group(
      {
        first_name: new FormControl('', Validators.required),
        last_name: new FormControl('', Validators.required),
        email: new FormControl('', Validators.required),
        phone_number: new FormControl('', Validators.required)
      },
      {
        validators: PhoneNumberValidator.Validate
      }
    );

    this.twoFactorFormGroup = this.formBuilder.group({
      twoFactorAuth: new FormControl(false),
      twoFactorMethod: new FormControl('sms')
    });

    await this.getProfile();
    this.subscription.add(
      this.twoFactorFormGroup.get('twoFactorAuth').valueChanges.subscribe((data) => {
        this.display2FactorMethod = this.current2FAEnabled === false && data === true;
        if (data === false && this.current2FAEnabled) {
          this.api.users.byId.patch(this.localStorage.userWithToken.id, { enable_2fa: data }).subscribe(
            () => {
              this.displaySuccessMessage('Two factor authentication has been disabled');
              this.current2FAEnabled = false;
            },
            (error) => {
              if (error.status === StatusCodes.FORBIDDEN) {
                this.modal.open.next({
                  modalId: 'authentication',
                  request: this.api.users.byId.patch(this.localStorage.userWithToken.id, { enable_2fa: data }),
                  scope: error.headers.get('scope'),
                  type: '2fa'
                });
              }
            },
            () => {
              this.router.navigated = false;
              this.router.navigate([this.router.url]);
            }
          );
        } else if (data === true && this.formGroup.get('phone_number').value) {
          if (this.twoFactorFormGroup.get('twoFactorMethod').value === TwoFactorMethod.VOICE) {
            this.voicePhoneNumber = this.formGroup.get('phone_number').value;
          }
        }
      })
    );

    this.subscription.add(
      this.twoFactorFormGroup.get('twoFactorMethod').valueChanges.subscribe((method) => {
        if (this.formGroup.get('phone_number').value) {
          if (method === TwoFactorMethod.VOICE) {
            this.voicePhoneNumber = this.formGroup.get('phone_number').value;
          }
        }
      })
    );
  }

  /**
   * Send a request to send the verification code to phone number.
   */
  smsRequest(): void {
    if (this.smsPhoneNumberForm.valid) {
      const smsPhoneNumber = this.smsPhoneNumberForm.get('phoneNumber').value;
      this.api.users.me.twoFactor.post(TwoFactorMethod.SMS, smsPhoneNumber).subscribe((data) => {
        data.smsPhoneNumber = smsPhoneNumber;
        this.modal.open.next({ modalId: 'verify2fa-code', modalData: data });
      });
    }
  }

  /**
   * Send a request to send verification call to phone number.
   */
  voiceRequest(): void {
    if (!this.voicePhoneNumber) return;
    this.api.users.me.twoFactor
      .post(TwoFactorMethod.VOICE, this.format.convertPhoneNumber(this.voicePhoneNumber))
      .subscribe((data) => {
        data.voicePhoneNumber = this.format.convertPhoneNumber(this.voicePhoneNumber);
        this.modal.open.next({ modalId: 'verify2fa-code', modalData: data });
      });
  }

  /**
   * Send a request to get the QR code for authenticator app.
   */
  authenticatorRequest(): void {
    this.api.users.me.twoFactor.post(TwoFactorMethod.APP).subscribe((data) => {
      this.modal.open.next({ modalId: 'authenticator-2fa', modalData: data });
    });
  }

  /**
   * Gets the profile information
   */
  async getProfile(): Promise<any> {
    try {
      this.updateProfileSuccessMessage = null;
      this.changePasswordSuccessMessage = null;
      this.api.users.byId.get(this.localStorage.userWithToken.id).subscribe((user) => {
        this.user = user;
        this.formGroup.patchValue(new UserDetailsForm(user, this.format));
        this.smsPhoneNumberForm.controls.phoneNumber.patchValue(user.phone_number);
      });
      await this.api.users.me
        .get()
        .toPromise()
        .then((userMe) => {
          if (userMe['two_factor_auth']) {
            this.twoFactorFormGroup.controls['twoFactorAuth'].setValue(true);
            this.current2FAEnabled = true;
          } else {
            this.current2FAEnabled = false;
            this.twoFactorFormGroup.controls['twoFactorAuth'].setValue(false);
          }
        });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Opens a modal for backup codes
   */
  displayBackupCodesModal() {
    this.modal.open.next({
      modalId: 'backup-codes'
    });
  }

  /**
   * Updates the profile information
   *
   * @param formValues The values from the form
   */
  updateProfile(formValues: UserDetailsForm): void {
    this.updateProfileSuccessMessage = null;
    this.changePasswordSuccessMessage = null;

    const newUserDetails = new UserPatch(formValues, this.format);

    for (const property in newUserDetails) {
      if (this.user[property] && newUserDetails[property] === this.user[property]) {
        delete newUserDetails[property];
      }
    }

    if (JSON.stringify(newUserDetails) === '{}') {
      this.updateProfileSuccessMessage = 'Details have been updated successfully.';
      return;
    }

    this.api.users.byId.patch(this.localStorage.userWithToken.id, newUserDetails).subscribe(
      (user) => {
        this.user = user;
        this.localStorage.userWithToken = { ...this.localStorage.userWithToken, ...user };
        this.formGroup.setValue(new UserDetailsForm(user, this.format));
        this.updateProfileSuccessMessage = 'Details have been updated successfully.';
      },
      (error) => {
        this.api.onFormError(error, this.formGroup);
        if (error.status === StatusCodes.FORBIDDEN && (newUserDetails.email || newUserDetails.phone_number)) {
          this.modal.open.next({
            modalId: 'authentication',
            request: this.api.users.byId.patch(this.localStorage.userWithToken.id, newUserDetails),
            scope: error.headers.get('scope'),
            type: 'profile'
          });
        }
      }
    );
  }

  /**
   * display the success message
   *
   * @param message the success message
   */
  private displaySuccessMessage(message, success: boolean = true): void {
    this.snackBar.open(message, '', {
      duration: 5000,
      panelClass: success ? ['success-bg', 'bold', 'light-color'] : ['alert-bg', 'bold', 'light-color']
    });
  }
}
